/**
 * Old healic.in URL → New site URL redirect map.
 * Every entry is a 301 redirect from a legacy URL to its content on the new site.
 */

export interface OldUrlRedirect {
  to: string;
  params?: Record<string, string>;
}

/** Direct path → redirect target. Used by the catch-all alias routes below. */
export const OLD_URL_REDIRECTS: Record<string, OldUrlRedirect> = {
  // Top-level page renames
  "/about-us": { to: "/about" },
  "/contact-us": { to: "/contact" },
  "/location": { to: "/locations" },
  "/location/agra": { to: "/locations/agra" },
  "/location/haridwar": { to: "/locations/haridwar" },
  "/location/indirapuram-ghaziabad": { to: "/locations/indirapuram-ghaziabad" },
  "/location/sohna-road-gurugram": { to: "/locations/sohna-road-gurugram" },
  "/patient-care/path-lab": { to: "/path-lab" },
  "/treatments/turp": { to: "/treatments/$slug", params: { slug: "prostate-surgery" } },


  // Specialty top-level pages
  "/general-surgery": { to: "/specialties/$slug", params: { slug: "general-surgery" } },
  "/ent": { to: "/specialties/$slug", params: { slug: "ent" } },
  "/urology": { to: "/specialties/$slug", params: { slug: "urology" } },
  "/orthopaedics": { to: "/specialties/$slug", params: { slug: "orthopaedics" } },
  "/gynecology": { to: "/specialties/$slug", params: { slug: "gynaecology" } },
  "/ophthalmologist": { to: "/specialties/$slug", params: { slug: "ophthalmology" } },
};

/** Old surgery slug → new content location. */
type Target =
  | { kind: "surgery"; slug: string }
  | { kind: "surgery-loc"; slug: string; location: string }
  | { kind: "treatment"; slug: string }
  | { kind: "specialty"; slug: string };

export const OLD_SURGERY_MAP: Record<string, Target> = {
  // Bare legacy specialty category pages (served directly at their old URLs)
  "general-surgery": { kind: "specialty", slug: "general-surgery" },
  "ent": { kind: "specialty", slug: "ent" },
  "urology": { kind: "specialty", slug: "urology" },
  "orthopaedics": { kind: "specialty", slug: "orthopaedics" },
  "gynecology": { kind: "specialty", slug: "gynaecology" },
  "ophthalmologist": { kind: "specialty", slug: "ophthalmology" },

  // /general-surgeries/:slug
  "general-surgeries/piles": { kind: "surgery", slug: "piles-hemorrhoidectomy" },
  "general-surgeries/varicose-veins": { kind: "surgery", slug: "varicose-veins" },
  "general-surgeries/circumcision": { kind: "surgery", slug: "circumcision" },
  "general-surgeries/cholecystectomy": { kind: "surgery", slug: "gallbladder-removal" },
  "general-surgeries/fissure-surgery": { kind: "treatment", slug: "anal-fissure" },
  "general-surgeries/fistula-surgery": { kind: "treatment", slug: "anal-fistula" },
  "general-surgeries/lipoma-removal-surgery": { kind: "treatment", slug: "lipoma-removal" },
  "general-surgeries/gynecomastia-surgery": { kind: "treatment", slug: "gynecomastia-surgery" },
  "general-surgeries/circumcision-surgery-in-gurgaon": { kind: "surgery-loc", slug: "circumcision", location: "sohna-road-gurugram" },
  "general-surgeries/circumcision-surgery-in-agra": { kind: "surgery-loc", slug: "circumcision", location: "agra" },
  "general-surgeries/circumcision-surgery-in-haridwar": { kind: "surgery-loc", slug: "circumcision", location: "haridwar" },
  "general-surgeries/circumcision-in-ghaziabad": { kind: "surgery-loc", slug: "circumcision", location: "indirapuram-ghaziabad" },

  // /ent-surgeries/:slug
  "ent-surgeries/tympanoplasty": { kind: "treatment", slug: "tympanoplasty" },
  "ent-surgeries/adenoidectomy": { kind: "treatment", slug: "adenoidectomy" },
  "ent-surgeries/septoplasty": { kind: "surgery", slug: "septoplasty" },
  "ent-surgeries/tonsillectomy": { kind: "surgery", slug: "tonsillectomy" },
  "ent-surgeries/nasal-bone-repair": { kind: "treatment", slug: "nasal-bone-repair" },
  "ent-surgeries/mastoidectomy": { kind: "treatment", slug: "mastoidectomy" },

  // /urology-surgeries/:slug
  "urology-surgeries/percutaneous-nephrolithotomy": { kind: "surgery", slug: "kidney-stone-removal" },
  "urology-surgeries/ureteroscopy": { kind: "treatment", slug: "ureteroscopy" },
  "urology-surgeries/transurethral-resection-of-prostate": { kind: "surgery", slug: "prostate-turp" },
  "urology-surgeries/transurethral-resection-of-bladder-tumor": { kind: "treatment", slug: "turbt" },
  "urology-surgeries/cystectomy": { kind: "treatment", slug: "cystectomy" },
  "urology-surgeries/urethroplasty": { kind: "treatment", slug: "urethroplasty" },

  // /orthopaedics-surgeries/:slug
  "orthopaedics-surgeries/joint-replacement": { kind: "treatment", slug: "joint-replacement" },
  "orthopaedics-surgeries/implant-removal": { kind: "treatment", slug: "implant-removal" },
  "orthopaedics-surgeries/forearm-plating": { kind: "treatment", slug: "forearm-plating" },
  "orthopaedics-surgeries/tendon-repair-surgery": { kind: "treatment", slug: "tendon-repair" },
  "orthopaedics-surgeries/wound-debridement": { kind: "treatment", slug: "wound-debridement" },
  "orthopaedics-surgeries/carpal-tunnel-release": { kind: "treatment", slug: "carpal-tunnel-release" },
  "orthopaedics-surgeries/trigger-finger-release": { kind: "treatment", slug: "trigger-finger-surgery" },
  "orthopaedics-surgeries/caudal-block": { kind: "treatment", slug: "caudal-block" },

  // /gynecology-surgeries/:slug
  "gynecology-surgeries/all-types-of-deliveries": { kind: "surgery", slug: "normal-c-section-delivery" },
  "gynecology-surgeries/dilation-and-curettage": { kind: "treatment", slug: "dandc-mtp-procedures" },
  "gynecology-surgeries/ovarian-cystectomy": { kind: "surgery", slug: "ovarian-cyst-removal" },
  "gynecology-surgeries/endometrial-ablation": { kind: "treatment", slug: "endometrial-ablation" },
  "gynecology-surgeries/laparoscopic-surgery": { kind: "treatment", slug: "laparoscopic-gynecology-surgery" },
  "gynecology-surgeries/hysterectomy": { kind: "surgery", slug: "hysterectomy" },

  // /ophthalmologist-surgeries/:slug
  "ophthalmologist-surgeries/cataract-surgery": { kind: "surgery", slug: "cataract-surgery" },
  "ophthalmologist-surgeries/full-thickness-penetrating-keratoplasty": { kind: "treatment", slug: "full-thickness-penetrating-keratoplasty-corneal-transplant" },
  "ophthalmologist-surgeries/vitrectomy": { kind: "treatment", slug: "vitrectomy" },
};

/** Legacy non-location procedure URLs consolidated onto their current canonical pages. */
export function getLegacyProcedureRedirects(): Record<string, string> {
  return Object.fromEntries(
    Object.entries(OLD_SURGERY_MAP).flatMap(([path, target]) => {
      if (target.kind === "surgery") {
        return [[`/${path}`, `/surgeries/${target.slug}`]];
      }
      if (target.kind === "treatment") {
        return [[`/${path}`, `/treatments/${target.slug}`]];
      }
      return [];
    }),
  );
}

/** Resolve an old URL (path with leading slash) to its new-site target. Returns null if unknown. */
export function resolveOldUrl(path: string): { to: string; params?: Record<string, string> } | null {
  const clean = path.replace(/\/+$/, "") || "/";

  // direct top-level
  if (OLD_URL_REDIRECTS[clean]) return OLD_URL_REDIRECTS[clean];

  // surgery-prefix routes
  const stripped = clean.startsWith("/") ? clean.slice(1) : clean;
  const target = OLD_SURGERY_MAP[stripped];
  if (!target) return null;

  switch (target.kind) {
    case "surgery":
      return { to: "/surgeries/$slug", params: { slug: target.slug } };
    case "surgery-loc":
      return { to: "/surgeries/$slug/$location", params: { slug: target.slug, location: target.location } };
    case "treatment":
      return { to: "/treatments/$slug", params: { slug: target.slug } };
    case "specialty":
      return { to: "/specialties/$slug", params: { slug: target.slug } };
  }
}
